import React from "react";
import { Account, SubscriptionStatus } from "@shared/models";
import { TableProperty } from "@shared/components/Table/interfaces";
import { Button, Icon, SubscriptionsStatus, TruncatedTextTooltip } from "@shared/components";
import { getFullName, getUSFormatedDateTime, hasAccountActiveSubscription } from "@shared/utils";
import { actions as sharedActions } from "@shared/store";
import { OVERLAY_TYPES } from "@shared/constants";
import styled from "styled-components";
import { useDeleteCompany } from "@containers/Contact/containers/ContactDetailsContainer/hooks";
import { ReactComponent as ReiSiftIcon } from "@assets/files/icons/apps/reisift-sm.svg";

import { store } from "../../../index";

const StyledButton = styled(Button)`
  word-break: keep-all;
`;

const StyledEditButton = styled(StyledButton)`
  margin-left: 16px;
`;

const handleNavigateToEditDetails = (e: React.MouseEvent, account: Account) => {
  e?.stopPropagation();

  store.dispatch(
    sharedActions.showOverlay({
      type: OVERLAY_TYPES.CREATE_EDIT_COMPANY,
      closeOnOverlayClick: true,
      props: {
        account,
        callback: () => {
          store.dispatch(sharedActions.hideOverlay());
        },
      },
    }),
  );
};

const getUniqueCountiesList = (item: Account) => {
  const subscriptionsCounties = item?.subscriptions
    ?.filter((s) => [SubscriptionStatus.ACTIVE, SubscriptionStatus.NON_RENEWING].includes(s.status))
    .map((s) => s.counties)
    .flat();
  const uniqueCounties = new Set<string>();
  subscriptionsCounties?.length && subscriptionsCounties.forEach((c) => uniqueCounties.add(c?.name));
  return uniqueCounties.size ? Array.from(uniqueCounties).join(", ") : "-";
};

const getUniqueStatesList = (item: Account) => {
  const subscriptionsStates = item?.subscriptions
    ?.filter((s) => [SubscriptionStatus.ACTIVE, SubscriptionStatus.NON_RENEWING].includes(s.status))
    .map((s) => s.counties)
    .flat();
  const uniqueStates = new Set<string>();
  subscriptionsStates?.length && subscriptionsStates.forEach((c) => uniqueStates.add(c?.state.name));
  return uniqueStates.size ? Array.from(uniqueStates).join(", ") : "-";
};

const DeleteButton = ({ account }: { account: Account }) => {
  const deleteCompany = useDeleteCompany();

  const handleDelete = (e: React.MouseEvent) => {
    e?.stopPropagation();

    deleteCompany(account);
  };

  return (
    <StyledEditButton onClick={handleDelete} buttonStyle="edit">
      <Icon type="delete" />
    </StyledEditButton>
  );
};

export const contactsTableProperties: TableProperty<Account>[] = [
  {
    title: "Name",
    key: "name",
    get: (item) => item.name,
    cellClassName: "title-cell",
    headerClassName: "title-header-cell",
  },
  {
    title: "State",
    key: "subscriptions",
    get: (item) => <TruncatedTextTooltip text={getUniqueStatesList(item)} />,
    cellClassName: "state-cell",
    headerClassName: "state-header-cell",
  },
  {
    title: "County",
    key: "subscriptions",
    get: (item) => <TruncatedTextTooltip text={getUniqueCountiesList(item)} />,
    cellClassName: "county-cell",
    headerClassName: "county-header-cell",
  },
  {
    title: "Primary User",
    key: "primary_user",
    get: (item) => (item?.primary_user ? getFullName(item.primary_user) : "-"),
    cellClassName: "primary-user-cell",
    headerClassName: "primary-user-header-cell",
  },
  {
    title: "Subscription Status",
    key: "subscriptions",
    get: (item) => {
      const accountHasActiveSubscription = hasAccountActiveSubscription(item);

      return (
        <>
          <SubscriptionsStatus status={accountHasActiveSubscription ? "active" : "inactive"} />

          {item.reisift && <ReiSiftIcon />}
        </>
      );
    },
    cellClassName: "status-cell",
    headerClassName: "status-header-cell",
  },
  {
    title: "Created on",
    key: "created_at",
    get: (item) => getUSFormatedDateTime(item.created_at),
    cellClassName: "table-date-cell",
    headerClassName: "table-date-header",
  },
  {
    title: "",
    key: "id",
    get: (item) => (
      <div className="actions-cell-wrapper">
        <StyledEditButton onClick={(e) => handleNavigateToEditDetails(e, item)} buttonStyle="edit">
          <Icon type="edit-blue" />
        </StyledEditButton>
        <DeleteButton account={item} />
      </div>
    ),
    cellClassName: "table-show-onhover actions-cell",
    headerClassName: "actions-header-cell",
  },
];
